import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { API } from '@/config';

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const activeCategory = searchParams.get('category') || 'all';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Ensure products are seeded in database (only seeds if empty)
        await axios.post(`${API}/seed`);
        
        // Fetch categories
        const catsResponse = await axios.get(`${API}/categories`);
        setCategories(['all', ...catsResponse.data]);
        
        // Fetch products
        const productsResponse = await axios.get(`${API}/products`, {
          params: { category: activeCategory !== 'all' ? activeCategory : undefined }
        });
        
        // Filter out bundles from "All Products" view - bundles only show in Bundles tab
        let filteredProducts = productsResponse.data;
        if (activeCategory === 'all') {
          filteredProducts = productsResponse.data.filter(p => p.category !== 'Bundles');
        }
        
        setProducts(filteredProducts);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activeCategory]);

  // Memoize filtered categories to avoid recalculation on every render
  const filteredCategories = useMemo(() => 
    categories.filter(cat => cat !== 'Bundles'),
    [categories]
  );

  const handleCategoryChange = (category) => {
    if (category === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category });
    }
  };

  return (
    <div className="min-h-screen py-8 md:py-12 bg-pattern">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="mb-8 md:mb-12 animate-fade-in">
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-pockett-gray mb-2 heading-underline inline-block">
            {activeCategory === 'Bundles' ? 'Bundle Deals' : activeCategory === 'all' ? 'Shop All' : activeCategory}
          </h1>
          <p className="text-muted-foreground mt-6">
            {activeCategory === 'Bundles' 
              ? 'Save more with our bundle packs' 
              : 'Browse our collection of handmade treasures'}
          </p>
        </div>

        {/* Category Filter */}
        <div className="mb-8 animate-fade-in animate-delay-100">
          <div className="flex flex-wrap gap-2">
            {filteredCategories.map((category) => (
              <Button
                key={category}
                variant={activeCategory === category ? "default" : "outline"}
                className={`rounded-full capitalize ${
                  activeCategory === category 
                    ? 'bg-pockett-purple hover:bg-pockett-purple/90' 
                    : ''
                }`}
                onClick={() => handleCategoryChange(category)}
                data-testid={`filter-${category.toLowerCase().replace(' ', '-')}`}
              >
                {category === 'all' ? 'All Products' : category}
              </Button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-pockett-purple" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground text-lg">
              No products found in this category.
            </p>
            <Button
              variant="outline"
              className="mt-4 rounded-full"
              onClick={() => handleCategoryChange('all')}
              data-testid="show-all-btn"
            >
              Show all products
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 md:gap-6">
            {products.map((product, index) => (
              <div 
                key={product.id}
                className="animate-fade-in"
                style={{ animationDelay: `${(index % 6) * 50}ms` }}
              >
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        )}

        {/* Product count */}
        {!loading && products.length > 0 && (
          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              Showing {products.length} product{products.length !== 1 ? 's' : ''}
              {activeCategory !== 'all' && ` in ${activeCategory}`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
